import { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { getDashboardStats, getRevenue } from '../../services/adminService';
import AdminUsers from '../admin/AdminUsers';
import AdminClasses from '../admin/AdminClasses';
import AdminPasses from '../admin/AdminPasses';
import AdminPassTypes from '../admin/AdminPassTypes';
import AdminClassTypes from '../admin/AdminClassTypes';
import AdminCourses from '../admin/AdminCourses';
import AdminSubscriptions from '../admin/AdminSubscriptions';
import AdminExpenses from '../admin/AdminExpenses';
import AdminVideos from '../admin/AdminVideos';
import AdminContentFolders from '../admin/AdminContentFolders';

const DASHBOARD_TABS = ['dashboard', 'classes', 'passes', 'pass types', 'class types', 'courses', 'users', 'subscriptions', 'expenses', 'content'];
const ACTIVE_TAB_STORAGE_KEY = 'adminDashboard.activeTab';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAppContext();
  const [activeTab, setActiveTab] = useState(() => {
    const stored = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    return DASHBOARD_TABS.includes(stored) ? stored : 'dashboard';
  });
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardView, setDashboardView] = useState('month'); // 'month' | 'year'
  const [passesPresetFilters, setPassesPresetFilters] = useState(null);
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth() + 1;
  const todayDay = today.getDate();
  const [{ year: viewYear, month: viewMonth }, setViewYM] = useState(() => ({
    year: todayYear,
    month: todayMonth,
  }));
  const isLiveCurrentMonth = viewYear === todayYear && viewMonth === todayMonth;
  const startOfYearDate = new Date(todayYear, 0, 1);
  const dayOfYear = Math.max(
    1,
    Math.floor((Date.now() - startOfYearDate.getTime()) / 86400000) + 1
  );
  const daysInYear =
    (new Date(todayYear + 1, 0, 1).getTime() - startOfYearDate.getTime()) / 86400000;
  const RUB_TO_ILS = Number(process.env.REACT_APP_RUB_TO_ILS) || 0.04;

  const daysInViewMonth = new Date(viewYear, viewMonth, 0).getDate();

  const shiftViewMonth = (delta) => {
    setViewYM(({ year, month }) => {
      let m = month + delta;
      let y = year;
      while (m < 1) {
        m += 12;
        y -= 1;
      }
      while (m > 12) {
        m -= 12;
        y += 1;
      }
      return { year: y, month: m };
    });
  };

  /** Monthly row: new pass sales vs subscription renewals (renewalCount from API). */
  const passesRenewalsCell = (m) => {
    const p = m.passCount ?? 0;
    const r = m.renewalCount ?? 0;
    return r > 0 ? `${p} / ${r}` : String(p);
  };

  /** Fallback when we lack class-day / weekday-weight data. */
  const projectedMonthByCalendar = (amountSoFar) => {
    if (amountSoFar == null || revenue?.year !== viewYear || !isLiveCurrentMonth) return null;
    if (!todayDay || todayDay > daysInViewMonth) return null;
    return Math.round((amountSoFar / todayDay) * daysInViewMonth);
  };

  const projectedYearByCalendar = (amountSoFar) => {
    if (amountSoFar == null || revenue?.year !== todayYear) return null;
    if (!dayOfYear || !daysInYear || dayOfYear > daysInYear) return null;
    return Math.round((amountSoFar / dayOfYear) * daysInYear);
  };

  const projIls = revenue?.projection?.ils;
  const projRub = revenue?.projection?.rub;

  /** ILS / studio: extrapolate by distinct days that have a scheduled class (not every calendar day). */
  const projectedMonthILS = (amountSoFar) => {
    if (amountSoFar == null || !isLiveCurrentMonth || revenue?.year !== viewYear) return null;
    if (
      projIls?.classDaysElapsedThisMonth > 0 &&
      projIls?.classDaysInMonth > 0
    ) {
      return Math.round(
        (amountSoFar / projIls.classDaysElapsedThisMonth) * projIls.classDaysInMonth
      );
    }
    return projectedMonthByCalendar(amountSoFar);
  };

  /** RUB / online: extrapolate using last 6 months’ weekday purchase mix (API falls back to flat weekdays if sample is tiny). */
  const projectedMonthRUB = (amountSoFar) => {
    if (amountSoFar == null || !isLiveCurrentMonth || revenue?.year !== viewYear) return null;
    if (
      projRub?.weightedElapsedThisMonth > 0 &&
      projRub?.weightedTotalThisMonth > 0
    ) {
      return Math.round(
        (amountSoFar / projRub.weightedElapsedThisMonth) * projRub.weightedTotalThisMonth
      );
    }
    return projectedMonthByCalendar(amountSoFar);
  };

  const projectedYearILS = (amountSoFar) => {
    if (amountSoFar == null || revenue?.year !== todayYear) return null;
    if (projIls?.classDaysElapsedThisYear > 0 && projIls?.classDaysInYear > 0) {
      return Math.round(
        (amountSoFar / projIls.classDaysElapsedThisYear) * projIls.classDaysInYear
      );
    }
    return projectedYearByCalendar(amountSoFar);
  };

  const projectedYearRUB = (amountSoFar) => {
    if (amountSoFar == null || revenue?.year !== todayYear) return null;
    if (
      projRub?.weightedElapsedThisYear > 0 &&
      projRub?.weightedTotalThisYear > 0
    ) {
      return Math.round(
        (amountSoFar / projRub.weightedElapsedThisYear) * projRub.weightedTotalThisYear
      );
    }
    return projectedYearByCalendar(amountSoFar);
  };

  const formatCurrencyNum = (n) => (Number(n) || 0).toLocaleString();

  useEffect(() => {
    // Wait for user to load
    if (user === undefined) {
      return;
    }

    setIsLoading(false);

    if (!user || user.role !== 'admin') {
      navigate('/not-found');
      return;
    }

    fetchStats();
    fetchRevenue();
  }, [user, token, navigate, dashboardView, viewYear, viewMonth]);

  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const data = await getDashboardStats(token, { view: dashboardView, year: viewYear, month: viewMonth });
      setStats(data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchRevenue = async () => {
    try {
      const data = await getRevenue(
        token,
        viewYear,
        dashboardView === 'month' ? { month: viewMonth } : {}
      );
      setRevenue(data);
    } catch (error) {
      console.error("Error fetching revenue:", error);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/not-found" replace />;
  }

  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="w-11/12 mx-auto py-6 mt-5">
        {/* Navigation Tabs */}
        <div className="flex gap-4 mb-6 border-b border-gray-200 overflow-x-auto">
          {DASHBOARD_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-semibold capitalize transition whitespace-nowrap ${
                activeTab === tab
                  ? 'text-brown border-b-2 border-brown'
                  : 'text-gray-600 hover:text-brown'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="flex w-full flex-wrap items-center gap-2">
                <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
                  <button
                    type="button"
                    onClick={() => setDashboardView('month')}
                    className={`px-3 py-1 text-xs font-semibold rounded-md transition ${
                      dashboardView === 'month' ? 'bg-brown text-white' : 'text-gray-600 hover:text-brown'
                    }`}
                  >
                    This month
                  </button>
                  <button
                    type="button"
                    onClick={() => setDashboardView('year')}
                    className={`px-3 py-1 text-xs font-semibold rounded-md transition ${
                      dashboardView === 'year' ? 'bg-brown text-white' : 'text-gray-600 hover:text-brown'
                    }`}
                  >
                    Year
                  </button>
                </div>
                {dashboardView === 'month' && (
                  <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white ml-auto md:ml-0">
                    <button
                      type="button"
                      onClick={() => shiftViewMonth(-1)}
                      className="px-2.5 py-1.5 text-sm font-semibold text-gray-600 hover:text-brown hover:bg-gray-50 rounded-l-lg transition"
                      aria-label="Previous month"
                    >
                      ‹
                    </button>
                    <span className="px-2 py-1.5 text-xs font-semibold text-gray-800 tabular-nums min-w-[6.5rem] text-center border-x border-gray-200">
                      {monthNames[viewMonth - 1]} {viewYear}
                    </span>
                    <button
                      type="button"
                      onClick={() => shiftViewMonth(1)}
                      className="px-2.5 py-1.5 text-sm font-semibold text-gray-600 hover:text-brown hover:bg-gray-50 rounded-r-lg transition"
                      aria-label="Next month"
                    >
                      ›
                    </button>
                  </div>
                )}
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats && (
                  <>
                    {dashboardView === 'month' ? (
                      <StatCard label="Classes" value={stats.classesThisMonth} onClick={() => setActiveTab('classes')} />
                    ) : (
                      <StatCard label="Total Classes" value={stats.totalClasses} onClick={() => setActiveTab('classes')} />
                    )}
                    {dashboardView === 'month' ? (
                      <StatCard
                        label="Active Passes"
                        value={
                          isLiveCurrentMonth
                            ? (stats.activeMultiPasses ?? 0) + (stats.activeSubscriptionMultiPasses ?? 0)
                            : stats.multiPassesSoldThisMonth ?? stats.passesSoldThisMonth
                        }
                        subValue={isLiveCurrentMonth ? stats.activeSubscriptionMultiPasses : null}
                        onClick={() => setActiveTab('passes')}
                      />
                    ) : (
                      <StatCard label="Total Students" value={stats.totalStudentsWithMultipass} onClick={() => setActiveTab('users')} />
                    )}
                    {dashboardView === 'year' && (
                      <StatCard label="Total Bookings" value={stats.totalBookingsThisYear} onClick={() => setActiveTab('bookings')} />
                    )}
                    {dashboardView === 'year' && (
                      <StatCard label="Course Purchases" value={stats.coursePurchasesThisYear ?? 0} onClick={() => setActiveTab('courses')} />
                    )}
                    {dashboardView === 'month' ? (
                      <StatCard
                        label="New Students"
                        value={stats.trialPassesThisMonth ?? 0}
                        onClick={() => setActiveTab('passes')}
                      />
                    ) : null}
                    {dashboardView === 'month' ? (
                      <StatCard
                        label="Online Club"
                        value={
                          isLiveCurrentMonth
                            ? stats.activeOnlineClubPasses ?? 0
                            : stats.onlineClubPassesThisMonth ?? 0
                        }
                        onClick={() => setActiveTab('passes')}
                      />
                    ) : null}
                    {dashboardView === 'month' ? (
                      <StatCard
                        label="Course Purchases"
                        value={stats.coursePurchasesThisMonth ?? 0}
                        onClick={() => setActiveTab('courses')}
                      />
                    ) : null}
                  </>
                )}
              </div>
            </div>

            {/* Revenue by currency: ILS (studio) vs RUB (online club) — never mix in one total */}
            {revenue && (
              <>
                {revenue.byCurrency ? (
                  <div className="space-y-6">
                    {['ILS', 'RUB'].map((code) => {
                      const block = revenue.byCurrency[code];
                      if (!block) return null;
                      // Hide RUB block when no RUB passes in year (avoid empty section)
                      if (
                        code === 'RUB' &&
                        block.totalPaid === 0 &&
                        block.totalUnpaid === 0 &&
                        (block.monthlyBreakdown || []).length === 0 &&
                        (block.byPassType || []).length === 0
                      ) {
                        return null;
                      }
                      const sym = block.symbol || (code === 'RUB' ? '₽' : '₪');
                      const isILS = code === 'ILS';
                      const ilsBlock = revenue.byCurrency?.ILS || null;
                      const rubBlock = revenue.byCurrency?.RUB || null;
                      const ilsSym = ilsBlock?.symbol || '₪';
                      const rubSym = rubBlock?.symbol || '₽';
                      const upcomingRentMonth = ilsBlock?.upcomingRentExpensesThisMonth ?? revenue.upcomingRentExpensesThisMonth ?? 0;
                      const totalExpenses = isILS ? (revenue.totalExpenses ?? 0) : 0;
                      const totalRentExpenses = isILS
                        ? (revenue.rentExpenses ?? ilsBlock?.totalRentExpenses ?? 0)
                        : 0;
                      const ilsThisMonth = (ilsBlock?.monthlyBreakdown || []).find((m) => m.month === viewMonth) || null;
                      const rubThisMonth = (rubBlock?.monthlyBreakdown || []).find((m) => m.month === viewMonth) || null;
                      const ilsNetRecordedThisMonth = Math.max(0, ilsThisMonth?.total ?? 0);
                      /** Unpaid passes sold in-period (e.g. pay on site); not in net/total, but expected cash — add to projections. */
                      const ilsUnpaidYtd = (ilsBlock?.monthlyBreakdown || [])
                        .filter((m) => m.month <= todayMonth)
                        .reduce((sum, m) => sum + (m.unpaid ?? 0), 0);
                      const rubUnpaidYtd = (rubBlock?.monthlyBreakdown || [])
                        .filter((m) => m.month <= todayMonth)
                        .reduce((sum, m) => sum + (m.unpaid ?? 0), 0);
                      const ilsUnpaidThisMonth = ilsThisMonth?.unpaid ?? 0;
                      const thisMonth = (block.monthlyBreakdown || []).find((m) => m.month === viewMonth);
                      const netSoFarThisYear = isILS
                        ? (block.monthlyBreakdown || [])
                            .filter((m) => m.month <= todayMonth)
                            .reduce((sum, m) => sum + (m.total ?? 0), 0)
                        : 0;
                      const projectedNetThisYear = isILS
                        ? (() => {
                            const p = projectedYearILS(netSoFarThisYear);
                            if (p == null) return null;
                            return p + ilsUnpaidYtd;
                          })()
                        : null;
                      const ilsProjectedMonthBase = projectedMonthILS(ilsNetRecordedThisMonth);
                      const projectedNetThisMonthILS =
                        ilsProjectedMonthBase == null
                          ? null
                          : (() => {
                              const paced =
                                isLiveCurrentMonth && revenue.year === todayYear
                                  ? Math.max(0, ilsProjectedMonthBase - upcomingRentMonth)
                                  : ilsProjectedMonthBase;
                              return paced + ilsUnpaidThisMonth;
                            })();
                      const projectedNetThisMonthRUB = (() => {
                        const base = projectedMonthRUB(rubThisMonth?.total ?? 0);
                        if (base == null) return null;
                        return base + (rubThisMonth?.unpaid ?? 0);
                      })();
                      const projectedNetThisMonth = isILS
                        ? projectedNetThisMonthILS
                        : projectedNetThisMonthRUB;
                      const projectedNetThisYearRUB = (() => {
                        const rubNetYtd = (rubBlock?.monthlyBreakdown || [])
                          .filter((m) => m.month <= todayMonth)
                          .reduce((sum, m) => sum + (m.total ?? 0), 0);
                        const p = projectedYearRUB(rubNetYtd);
                        if (p == null) return null;
                        return p + rubUnpaidYtd;
                      })();
                      const monthLabel = monthNames[viewMonth - 1];
                      return (
                        <div key={code} className="space-y-3">
                          <h3 className="text-sm font-semibold text-gray-700">
                            {code === 'RUB' ? 'Online club (RUB)' : 'Studio & other (ILS)'}
                          </h3>
                          {dashboardView === 'year' && (
                            <div className="space-y-2">
                              <p className="text-xs font-semibold text-gray-500">Year ({revenue.year})</p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3 md:p-4">
                                  <p className="text-green-600 text-xs md:text-sm font-semibold">Total Paid ({revenue.year})</p>
                                  <p className="text-lg md:text-2xl font-bold text-green-700">
                                    {sym}{block.totalPaid?.toLocaleString() || 0}
                                  </p>
                                </div>
                                {isILS && (
                                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 md:p-4">
                                    <p className="text-orange-600 text-xs md:text-sm font-semibold">Total Expenses (ILS)</p>
                                    <p className="text-lg md:text-2xl font-bold text-orange-700">
                                      ₪{totalExpenses.toLocaleString()}
                                    </p>
                                    <p className="text-xs text-orange-700 mt-0.5">Rent: ₪{totalRentExpenses.toLocaleString()}</p>
                                  </div>
                                )}
                                {code === 'ILS' && (
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4 min-h-[130px] flex flex-col">
                                    <p className="text-blue-600 text-xs md:text-sm font-semibold">Net</p>
                                    <div className="space-y-1 mt-1">
                                      <div>
                                        <p className="text-lg md:text-2xl font-bold text-blue-700">
                                          ₪{formatCurrencyNum((ilsBlock?.totalRevenue ?? 0) + (rubBlock?.totalRevenue ?? 0) * RUB_TO_ILS)}
                                        </p>
                                      </div>
                                      <p className="text-xs md:text-sm font-bold text-blue-700">
                                        {ilsSym}{formatCurrencyNum(ilsBlock?.totalRevenue ?? 0)} + {rubSym}{formatCurrencyNum(rubBlock?.totalRevenue ?? 0)}
                                      </p>
                                    </div>
                                  </div>
                                )}
                                {code === 'ILS' && (projectedNetThisYear !== null || projectedNetThisYearRUB !== null) && (
                                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 md:p-4 min-h-[130px] flex flex-col">
                                    <p className="text-purple-700 text-xs md:text-sm font-semibold">Projected net</p>
                                    <div className="space-y-1 mt-1">
                                      <div>
                                        <p className="text-lg md:text-2xl font-bold text-purple-800">
                                          ₪{formatCurrencyNum((projectedNetThisYear ?? 0) + (projectedNetThisYearRUB ?? 0) * RUB_TO_ILS)}
                                        </p>
                                      </div>
                                      <p className="text-xs md:text-sm font-bold text-purple-800">
                                        {ilsSym}{formatCurrencyNum(projectedNetThisYear ?? 0)} + {rubSym}{formatCurrencyNum(projectedNetThisYearRUB ?? 0)}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {dashboardView === 'month' && (
                            <div className="space-y-4">
                              <p className="text-xs font-semibold text-gray-500">
                                {viewYear === todayYear && viewMonth === todayMonth ? 'This month' : 'Month'}{' '}
                                ({monthLabel} {viewYear})
                              </p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3 md:p-4">
                                  <p className="text-green-600 text-xs md:text-sm font-semibold">Paid</p>
                                  <p className="text-lg md:text-2xl font-bold text-green-700">
                                    {sym}{(thisMonth?.paid ?? 0).toLocaleString()}
                                  </p>
                                </div>
                                {isILS && (
                                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 md:p-4">
                                    <p className="text-orange-600 text-xs md:text-sm font-semibold">Expenses</p>
                                    <p className="text-lg md:text-2xl font-bold text-orange-700">
                                      ₪{(thisMonth?.expenses ?? 0).toLocaleString()}
                                    </p>
                                    <p className="text-xs text-orange-700 mt-0.5">
                                      Rent: ₪{(thisMonth?.rentExpenses ?? 0).toLocaleString()}
                                    </p>
                                  </div>
                                )}
                                {code === 'ILS' && (
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4 min-h-[130px] flex flex-col">
                                    <p className="text-blue-600 text-xs md:text-sm font-semibold">Net</p>
                                    <div className="space-y-1 mt-1">
                                      <div>
                                        <p className="text-lg md:text-2xl font-bold text-blue-700">
                                          ₪{formatCurrencyNum(ilsNetRecordedThisMonth + (rubThisMonth?.total ?? 0) * RUB_TO_ILS)}
                                        </p>
                                      </div>
                                      <p className="text-xs md:text-sm font-bold text-blue-700">
                                        {ilsSym}{formatCurrencyNum(ilsNetRecordedThisMonth)} + {rubSym}{formatCurrencyNum(rubThisMonth?.total ?? 0)}
                                      </p>
                                    </div>
                                  </div>
                                )}
                                {code === 'ILS' && (projectedNetThisMonthILS !== null || projectedNetThisMonthRUB !== null) && (
                                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 md:p-4 min-h-[130px] flex flex-col">
                                    <p className="text-purple-700 text-xs md:text-sm font-semibold">Projected net</p>
                                    <div className="space-y-1 mt-1">
                                      <div>
                                        <p className="text-lg md:text-2xl font-bold text-purple-800">
                                          ₪{formatCurrencyNum((projectedNetThisMonthILS ?? 0) + (projectedNetThisMonthRUB ?? 0) * RUB_TO_ILS)}
                                        </p>
                                      </div>
                                      <p className="text-xs md:text-sm font-bold text-purple-800">
                                        {ilsSym}{formatCurrencyNum(projectedNetThisMonthILS ?? 0)} + {rubSym}{formatCurrencyNum(projectedNetThisMonthRUB ?? 0)}
                                      </p>
                                    </div>
                                    {/* {revenue.year === currentYear && upcomingRentMonth > 0 && (
                                      <p className="text-[10px] md:text-xs text-purple-600 mt-1 leading-snug">
                                        ILS projection subtracts scheduled studio rent still to come this month (₪
                                        {upcomingRentMonth.toLocaleString()}).
                                      </p>
                                    )} */}
                                  </div>
                                )}
                              </div>

                              {(block.thisMonthByPassType || []).length > 0 && (
                                <div className="bg-white rounded-lg shadow overflow-hidden">
                                  <div className="p-4 border-b border-gray-200">
                                    <h4 className="font-semibold text-sm">By pass type ({code})</h4>
                                  </div>
                                  {/* Desktop: Table */}
                                  <div className="hidden md:block overflow-x-auto">
                                    <table className="w-full">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">Pass type</th>
                                          <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Sold</th>
                                          <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Paid</th>
                                          <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Unpaid</th>
                                          <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Total</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100">
                                        {(block.thisMonthByPassType || []).map((pt) => (
                                          <tr
                                            key={`${code}-m-${pt.id || pt.name}`}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => {
                                              const start = new Date(viewYear, viewMonth - 1, 1, 0, 0, 0, 0);
                                              const end = new Date(viewYear, viewMonth, 0, 23, 59, 59, 999);
                                              setPassesPresetFilters({
                                                passTypeId: pt.id || '',
                                                purchasedFrom: start.toISOString(),
                                                purchasedTo: end.toISOString(),
                                              });
                                              setActiveTab('passes');
                                            }}
                                            onKeyDown={(e) => {
                                              if (e.key !== 'Enter') return;
                                              const start = new Date(viewYear, viewMonth - 1, 1, 0, 0, 0, 0);
                                              const end = new Date(viewYear, viewMonth, 0, 23, 59, 59, 999);
                                              setPassesPresetFilters({
                                                passTypeId: pt.id || '',
                                                purchasedFrom: start.toISOString(),
                                                purchasedTo: end.toISOString(),
                                              });
                                              setActiveTab('passes');
                                            }}
                                          >
                                            <td className="px-4 py-2 text-sm font-medium capitalize">{pt.name}</td>
                                            <td className="px-4 py-2 text-sm text-right">{pt.count}</td>
                                            <td className="px-4 py-2 text-sm text-right text-green-600">
                                              {sym}{(pt.paid ?? 0).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-right text-yellow-600">
                                              {sym}{(pt.unpaid ?? 0).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-right font-semibold">
                                              {sym}{(pt.total ?? 0).toLocaleString()}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  {/* Mobile: Cards */}
                                  <div className="md:hidden divide-y divide-gray-100">
                                    {(block.thisMonthByPassType || []).map((pt) => (
                                      <div key={`${code}-m-${pt.name}`} className="p-4">
                                        <p className="text-sm font-semibold text-gray-900 capitalize mb-2">{pt.name}</p>
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                          <span className="text-gray-500">Sold</span>
                                          <span className="text-right font-semibold">{pt.count}</span>
                                          <span className="text-gray-500">Paid</span>
                                          <span className="text-right text-green-700">{sym}{(pt.paid ?? 0).toLocaleString()}</span>
                                          <span className="text-gray-500">Unpaid</span>
                                          <span className="text-right text-yellow-700">{sym}{(pt.unpaid ?? 0).toLocaleString()}</span>
                                          <span className="text-gray-500 font-medium">Total</span>
                                          <span className="text-right font-semibold">{sym}{(pt.total ?? 0).toLocaleString()}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {(block.thisMonthByCourse || []).length > 0 && (
                                <div className="bg-white rounded-lg shadow overflow-hidden">
                                  <div className="p-4 border-b border-gray-200">
                                    <h4 className="font-semibold text-sm">By course ({code})</h4>
                                  </div>
                                  {/* Desktop: Table */}
                                  <div className="hidden md:block overflow-x-auto">
                                    <table className="w-full">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">Course</th>
                                          <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Sold</th>
                                          <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Paid</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100">
                                        {(block.thisMonthByCourse || []).map((c) => (
                                          <tr key={`${code}-cm-${c.id || c.name}`} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 text-sm font-medium">{c.name}</td>
                                            <td className="px-4 py-2 text-sm text-right">{c.count}</td>
                                            <td className="px-4 py-2 text-sm text-right text-green-600">
                                              {sym}{(c.paid ?? 0).toLocaleString()}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  {/* Mobile: Cards */}
                                  <div className="md:hidden divide-y divide-gray-100">
                                    {(block.thisMonthByCourse || []).map((c) => (
                                      <div key={`${code}-cm-${c.name}`} className="p-4">
                                        <p className="text-sm font-semibold text-gray-900 mb-2">{c.name}</p>
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                          <span className="text-gray-500">Sold</span>
                                          <span className="text-right font-semibold">{c.count}</span>
                                          <span className="text-gray-500">Paid</span>
                                          <span className="text-right text-green-700">{sym}{(c.paid ?? 0).toLocaleString()}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          {/* Monthly table for this currency */}
                          {dashboardView === 'year' && block.monthlyBreakdown?.length > 0 && (
                            <div className="bg-white rounded-lg shadow overflow-hidden">
                              <div className="p-4 border-b border-gray-200">
                                <h4 className="font-semibold text-sm">Monthly ({code}) — {revenue.year}</h4>
                              </div>
                              {/* Desktop: Table */}
                              <div className="hidden md:block overflow-x-auto">
                                <table className="w-full">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">Month</th>
                                      <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Passes / renewals</th>
                                      <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Paid</th>
                                      <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Unpaid</th>
                                      {isILS && (
                                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Expenses</th>
                                      )}
                                      <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Net</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {block.monthlyBreakdown.map((month) => (
                                      <tr key={`${code}-${month.month}`} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 text-sm font-medium">{monthNames[month.month - 1]}</td>
                                        <td className="px-4 py-2 text-sm text-right">{passesRenewalsCell(month)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-green-600">
                                          {sym}{month.paid.toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-right text-yellow-600">
                                          {sym}{month.unpaid.toLocaleString()}
                                        </td>
                                        {isILS && (
                                          <td className="px-4 py-2 text-sm text-right text-orange-600">
                                            ₪{(month.expenses ?? 0).toLocaleString()}
                                          </td>
                                        )}
                                        <td className="px-4 py-2 text-sm text-right font-semibold">
                                          {sym}{month.total.toLocaleString()}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {/* Mobile: Cards */}
                              <div className="md:hidden divide-y divide-gray-100">
                                {block.monthlyBreakdown.map((month) => (
                                  <div key={`${code}-${month.month}`} className="p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-2">{monthNames[month.month - 1]}</p>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                                      <span className="text-gray-500">Passes / renewals</span>
                                      <span className="text-right">{passesRenewalsCell(month)}</span>
                                      <span className="text-gray-500">Paid</span>
                                      <span className="text-right text-green-600">{sym}{month.paid.toLocaleString()}</span>
                                      <span className="text-gray-500">Unpaid</span>
                                      <span className="text-right text-yellow-600">{sym}{month.unpaid.toLocaleString()}</span>
                                      {isILS && (
                                        <>
                                          <span className="text-gray-500">Expenses</span>
                                          <span className="text-right text-orange-600">₪{(month.expenses ?? 0).toLocaleString()}</span>
                                        </>
                                      )}
                                      <span className="text-gray-500 font-medium">Net</span>
                                      <span className="text-right font-semibold">{sym}{month.total.toLocaleString()}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* By pass type for this currency */}
                          {dashboardView === 'year' && block.byPassType?.length > 0 && (
                            <div className="bg-white rounded-lg shadow overflow-hidden">
                              <div className="p-4 border-b border-gray-200">
                                <h4 className="font-semibold text-sm">By pass type ({code})</h4>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">Pass type</th>
                                      <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Sold</th>
                                      <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Paid</th>
                                      <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Unpaid</th>
                                      <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {block.byPassType.map((pt) => (
                                      <tr key={`${code}-${pt.name}`} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 text-sm font-medium capitalize">{pt.name}</td>
                                        <td className="px-4 py-2 text-sm text-right">{pt.count}</td>
                                        <td className="px-4 py-2 text-sm text-right text-green-600">
                                          {sym}{pt.paid.toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-right text-yellow-600">
                                          {sym}{pt.unpaid.toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-right font-semibold">
                                          {sym}{pt.total.toLocaleString()}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                          {/* By course for this currency */}
                          {dashboardView === 'year' && block.byCourse?.length > 0 && (
                            <div className="bg-white rounded-lg shadow overflow-hidden">
                              <div className="p-4 border-b border-gray-200">
                                <h4 className="font-semibold text-sm">By course ({code})</h4>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">Course</th>
                                      <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Sold</th>
                                      <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">Paid</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {block.byCourse.map((c) => (
                                      <tr key={`${code}-c-${c.id || c.name}`} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 text-sm font-medium">{c.name}</td>
                                        <td className="px-4 py-2 text-sm text-right">{c.count}</td>
                                        <td className="px-4 py-2 text-sm text-right text-green-600">
                                          {sym}{(c.paid ?? 0).toLocaleString()}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Legacy single-currency layout */
                  (() => {
                    const totalExpenses = (revenue.monthlyBreakdown || []).reduce((sum, m) => sum + (m.expenses ?? 0), 0);
                    const thisMonth = (revenue.monthlyBreakdown || []).find((m) => m.month === viewMonth);
                    const legacyUpcomingMonth = revenue.upcomingRentExpensesThisMonth ?? 0;
                    const legacyProjBase = projectedMonthILS(thisMonth?.total ?? 0);
                    const legacyUnpaidMonth = thisMonth?.unpaid ?? 0;
                    const projectedNetThisMonth =
                      legacyProjBase == null
                        ? null
                        : (isLiveCurrentMonth && revenue.year === todayYear
                            ? Math.max(0, legacyProjBase - legacyUpcomingMonth)
                            : legacyProjBase) + legacyUnpaidMonth;
                    return (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 md:p-4">
                          <p className="text-green-600 text-xs md:text-sm font-semibold">Total Paid ({revenue.year})</p>
                          <p className="text-lg md:text-2xl font-bold text-green-700">
                            ₪{revenue.totalPaid?.toLocaleString() || 0}
                          </p>
                        </div>
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 md:p-4">
                          <p className="text-orange-600 text-xs md:text-sm font-semibold">Total Expenses</p>
                          <p className="text-lg md:text-2xl font-bold text-orange-700">
                            ₪{totalExpenses.toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                          <p className="text-blue-600 text-xs md:text-sm font-semibold">Total Revenue</p>
                          <p className="text-lg md:text-2xl font-bold text-blue-700">
                            ₪{revenue.totalRevenue?.toLocaleString() || 0}
                          </p>
                          <p className="text-xs text-blue-500 mt-0.5 md:mt-1">{revenue.totalPasses} passes sold</p>
                        </div>
                        {projectedNetThisMonth !== null && (
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 md:p-4 md:col-span-2">
                            <p className="text-purple-700 text-xs md:text-sm font-semibold">
                              Projected net
                            </p>
                            <p className="text-lg md:text-2xl font-bold text-purple-800">
                              ₪{projectedNetThisMonth.toLocaleString()}
                            </p>
                            <p className="text-xs text-purple-600 mt-0.5 md:mt-1">
                              Based on net so far in {monthNames[viewMonth - 1]}
                              {revenue.projection
                                ? ' — paced by days with scheduled classes when available.'
                                : ''}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}

                {/* Legacy monthly + byPassType only when API has no byCurrency */}
                {!revenue.byCurrency && (
                <>
                {/* Monthly Revenue Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="font-semibold">Monthly Revenue ({revenue.year})</h3>
                  </div>
                  {/* Desktop: Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Month</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Passes / renewals</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Paid</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Unpaid</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Expenses</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {revenue.monthlyBreakdown?.length > 0 ? (
                          revenue.monthlyBreakdown.map((month) => (
                            <tr key={month.month} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium">{monthNames[month.month - 1]}</td>
                              <td className="px-4 py-3 text-sm text-right">{passesRenewalsCell(month)}</td>
                              <td className="px-4 py-3 text-sm text-right text-green-600">
                                ₪{month.paid.toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-yellow-600">
                                ₪{month.unpaid.toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-orange-600">
                                ₪{(month.expenses ?? 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-sm text-right font-semibold">
                                ₪{month.total.toLocaleString()}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                              No revenue data yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile: Cards — Passes, Paid, Unpaid, Expenses, Total (net) */}
                  <div className="md:hidden divide-y divide-gray-100">
                    {revenue.monthlyBreakdown?.length > 0 ? (
                      revenue.monthlyBreakdown.map((month) => (
                        <div key={month.month} className="p-4">
                          <p className="text-sm font-semibold text-gray-900 mb-2">{monthNames[month.month - 1]}</p>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                            <span className="text-gray-500">Passes / renewals</span>
                            <span className="text-right">{passesRenewalsCell(month)}</span>
                            <span className="text-gray-500">Paid</span>
                            <span className="text-right text-green-600">₪{month.paid.toLocaleString()}</span>
                            <span className="text-gray-500">Unpaid</span>
                            <span className="text-right text-yellow-600">₪{month.unpaid.toLocaleString()}</span>
                            <span className="text-gray-500">Expenses</span>
                            <span className="text-right text-orange-600">₪{(month.expenses ?? 0).toLocaleString()}</span>
                            <span className="text-gray-500 font-medium">Total (net)</span>
                            <span className="text-right font-semibold">₪{month.total.toLocaleString()}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-8 text-center text-gray-500 text-sm">No revenue data yet</div>
                    )}
                  </div>
                </div>

                {/* Revenue by Pass Type */}
                {revenue.byPassType?.length > 0 && (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="font-semibold">Revenue by Pass Type</h3>
                    </div>
                    {/* Desktop: Table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Pass Type</th>
                            <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Sold</th>
                            <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Paid</th>
                            <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Unpaid</th>
                            <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {revenue.byPassType.map((passType) => (
                            <tr key={passType.name} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium capitalize">{passType.name}</td>
                              <td className="px-4 py-3 text-sm text-right">{passType.count}</td>
                              <td className="px-4 py-3 text-sm text-right text-green-600">
                                ₪{passType.paid.toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-yellow-600">
                                ₪{passType.unpaid.toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-sm text-right font-semibold">
                                ₪{passType.total.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile: Cards */}
                    <div className="md:hidden divide-y divide-gray-100">
                      {revenue.byPassType.map((passType) => (
                        <div key={passType.name} className="p-4">
                          <p className="text-sm font-semibold text-gray-900 capitalize mb-2">{passType.name}</p>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            <span className="text-gray-500">Sold</span>
                            <span className="text-right">{passType.count}</span>
                            <span className="text-gray-500">Paid</span>
                            <span className="text-right text-green-600">₪{passType.paid.toLocaleString()}</span>
                            <span className="text-gray-500">Unpaid</span>
                            <span className="text-right text-yellow-600">₪{passType.unpaid.toLocaleString()}</span>
                            <span className="text-gray-500 font-medium">Total</span>
                            <span className="text-right font-semibold">₪{passType.total.toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </>
                )}
              </>
            )}
          </div>
        )}

        {/* Expenses Tab */}
        {activeTab === 'expenses' && <AdminExpenses token={token} />}

        {/* Users Tab */}
        {activeTab === 'users' && <AdminUsers token={token} />}

        {/* Classes Tab */}
        {activeTab === 'classes' && <AdminClasses token={token} />}

        {/* Passes Tab */}
        {activeTab === 'passes' && <AdminPasses token={token} presetFilters={passesPresetFilters} />}

        {/* Pass Types Tab */}
        {activeTab === 'pass types' && <AdminPassTypes token={token} />}

        {/* Class Types Tab */}
        {activeTab === 'class types' && <AdminClassTypes token={token} />}

        {/* Courses Tab */}
        {activeTab === 'courses' && <AdminCourses token={token} />}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && <AdminSubscriptions token={token} />}

        {/* Content Tab */}
        {activeTab === 'content' && (
          <div className="space-y-6">
            <AdminContentFolders token={token} />
            <AdminVideos token={token} />
          </div>
        )}
      </div>
    </main>
  );
}

function StatCard({ label, value, onClick, subValue }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="bg-white rounded-lg shadow p-6 text-left w-full hover:shadow-md hover:ring-2 hover:ring-brown/20 transition focus:outline-none focus:ring-2 focus:ring-brown"
    >
      <p className="text-gray-600 text-sm font-semibold">{label}</p>
      <p className="text-3xl font-bold text-brown mt-2">{value}</p>
      {subValue != null && (
        <p className="text-xs text-gray-400 mt-1">{subValue} subscription{subValue !== 1 ? 's' : ''}</p>
      )}
    </button>
  );
}